'use client';

import { Button } from '@mui/material';
import Link from 'next/link';
import { FC, MouseEvent, useCallback } from 'react';

import { useGlobalModalContext } from '@abg/web-core/context/global-modal';

import type { GlobalLinkProps } from './types';
import { parseHref } from './utils';

/*
  href should look like:
  "modal://modalTypeA" example for modal, if something else then its link.
 */
export const GlobalLink: FC<GlobalLinkProps> = ({
  dataTestId,
  disableRipple = false,
  disabled,
  href,
  children,
  className,
  sx = {},
  rel,
  target,
  callbackFn,
  classes,
  variant,
  dataProps,
  onAnalyticsClick,
  ariaLabel,
}) => {
  const { openModal } = useGlobalModalContext();
  const parsed = parseHref(href);

  const handleOpen = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      openModal(parsed?.value);
      onAnalyticsClick?.(event);
      callbackFn?.(event);
    },
    [callbackFn, openModal, parsed?.value],
  );

  if (parsed.type === 'modal') {
    return (
      <>
        <Button
          disabled={Boolean(disabled)}
          disableRipple={disableRipple}
          sx={sx}
          className={className || ''}
          classes={classes || {}}
          aria-label={ariaLabel ?? 'modal-trigger'}
          data-testid={dataTestId || `global-button`}
          variant={variant ?? 'contained'}
          onClick={handleOpen}
          {...dataProps}
        >
          {children}
        </Button>
      </>
    );
  }

  return (
    <Button
      disabled={Boolean(disabled)}
      component={Link}
      prefetch={false}
      variant={variant ?? 'contained'}
      className={className || ''}
      classes={classes || {}}
      aria-label={ariaLabel ?? 'modal-trigger'}
      data-testid={dataTestId || `global-link`}
      href={parsed.value}
      rel={rel}
      target={target}
      sx={sx}
      {...dataProps}
    >
      {children}
    </Button>
  );
};
